import { useMemo, useState } from "react";
import { Button, Input, Modal, Select, Textarea } from "@legal-500/legal500-design-system";
import { EventImageUploadField } from "@/components/Events/EventImageUploadField";
import {
  eventsService,
  type EventPayload,
  type EventStatus,
  type EventType,
  type StaffEvent,
} from "@/api/events.service";
import type { ImageRef } from "@/api/events/files";
import {
  EVENT_STATUS_OPTIONS,
  EVENT_TYPE_OPTIONS,
  isPastStartDate,
  locationShortRequiredForType,
  PAST_DATE_WARNING,
  timeZoneOptions,
  validateEventForm,
  type EventFormErrors,
  type EventFormValues,
} from "./eventValidation";
import { describeEventSaveConsequence } from "./eventVisibility";
import styles from "./Events.module.css";

/** The ids the submit button points `aria-describedby` at, so the state reaches the button. */
const VISIBILITY_BADGE_ID = "event-visibility-state";
const VISIBILITY_CONSEQUENCE_ID = "event-visibility-consequence";
/** Why Save is off while a featured image is still uploading. */
const UPLOAD_PENDING_ID = "event-image-upload-pending";

export type EventFormModalProps = {
  isOpen: boolean;
  /** The event being edited, or null to create a new one. */
  event: StaffEvent | null;
  onClose: () => void;
  onSaved: (message: string) => void;
};

const EMPTY_FORM: EventFormValues = {
  title: "",
  type: "",
  startDate: "",
  startTime: "",
  timezone: "Europe/London",
  locationShort: "",
  venueAddress: "",
  featuredImage: null,
  linkUrl: "",
  // A new event starts as a draft: putting it on the public site is a deliberate act, never
  // the side effect of opening the form (design D5).
  status: "draft",
};

function toFormValues(event: StaffEvent | null): EventFormValues {
  if (!event) return { ...EMPTY_FORM };
  return {
    title: event.title,
    type: event.type,
    startDate: event.startDate,
    // <input type="time"> shows HH:MM; content-api returns HH:MM:SS.
    startTime: event.startTime.slice(0, 5),
    timezone: event.timezone,
    locationShort: event.locationShort ?? "",
    venueAddress: event.venueAddress ?? "",
    featuredImage: event.featuredImage,
    linkUrl: event.linkUrl,
    status: event.status,
  };
}

function toPayload(values: EventFormValues): EventPayload {
  return {
    title: values.title.trim(),
    type: values.type as EventType,
    startDate: values.startDate,
    startTime: values.startTime,
    timezone: values.timezone,
    locationShort: values.locationShort.trim() === "" ? null : values.locationShort.trim(),
    venueAddress: values.venueAddress.trim() === "" ? null : values.venueAddress.trim(),
    featuredImage: values.featuredImage,
    linkUrl: values.linkUrl.trim(),
    status: values.status,
  };
}

export function EventFormModal({ isOpen, event, onClose, onSaved }: EventFormModalProps) {
  // Re-keyed by the caller on open, so the initial state is the event being edited.
  const [values, setValues] = useState<EventFormValues>(() => toFormValues(event));
  const [errors, setErrors] = useState<EventFormErrors>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  // The past-date rule warns; it does not block. This is the "confirm and proceed" latch.
  const [pastDateConfirmed, setPastDateConfirmed] = useState(false);
  const [awaitingPastDateConfirm, setAwaitingPastDateConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  // Reported by EventImageUploadField, because saving mid-upload would persist the old image.
  const [imageUploading, setImageUploading] = useState(false);

  const zones = useMemo(() => timeZoneOptions(event?.timezone), [event?.timezone]);
  const startDateIsPast = isPastStartDate(values.startDate);
  const locationRequired = locationShortRequiredForType(values.type);

  const visibility = describeEventSaveConsequence({
    // The SAVED status from the prop, never `values.status` — that is what makes the badge
    // honest about what is on the public page right now rather than what has been picked.
    saved: event?.status ?? null,
    pending: values.status,
  });

  // The upload reason joins the description only while it applies, for the moment the button
  // re-enables and takes focus — the live region below is what announces it as it appears.
  const submitDescribedBy = [
    VISIBILITY_BADGE_ID,
    VISIBILITY_CONSEQUENCE_ID,
    ...(imageUploading ? [UPLOAD_PENDING_ID] : []),
  ].join(" ");

  const setField = <K extends keyof EventFormValues>(key: K, value: EventFormValues[K]) => {
    setValues(current => ({ ...current, [key]: value }));
    setErrors(current => ({ ...current, [key]: undefined }));
    if (key === "startDate") {
      // A fresh date deserves a fresh decision — otherwise one confirmation would wave
      // through every later back-dating in the same sitting.
      setPastDateConfirmed(false);
      setAwaitingPastDateConfirm(false);
    }
  };

  const persist = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const payload = toPayload(values);
      if (event) {
        await eventsService.update(event.id, payload);
        onSaved(`"${payload.title}" updated.`);
      } else {
        await eventsService.create(payload);
        onSaved(`"${payload.title}" created.`);
      }
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "The event could not be saved");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (submitEvent: React.FormEvent) => {
    submitEvent.preventDefault();
    // Submitting now would persist the PREVIOUS image ref and close the form, leaving the
    // in-flight upload attached to nothing. Guarded here as well as on the button, so
    // correctness does not rest on the DOM's `disabled` semantics.
    if (imageUploading) return;

    const nextErrors = validateEventForm(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    if (startDateIsPast && !pastDateConfirmed) {
      setAwaitingPastDateConfirm(true);
      return;
    }

    await persist();
  };

  const confirmPastDateAndSave = async () => {
    // Same reason as in handleSubmit: saving now would persist the old image ref and close
    // the form. The past-date path reaches `persist` without going through handleSubmit.
    if (imageUploading) return;
    setPastDateConfirmed(true);
    setAwaitingPastDateConfirm(false);
    await persist();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={event ? "Edit event" : "New event"}
      size="mediumLarge"
    >
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <Input
          label="Title"
          value={values.title}
          error={errors.title}
          fullWidth
          onChange={e => setField("title", e.target.value)}
        />

        <div className={styles.formRow}>
          <Select
            label="Event type"
            value={values.type}
            error={errors.type}
            fullWidth
            options={[{ value: "", label: "Select an event type…" }, ...EVENT_TYPE_OPTIONS]}
            onChange={e => setField("type", e.target.value as EventType | "")}
          />
          {/*
            Deliberately no `aria-describedby` here: Select computes its own from `error` and
            `helperText` and then spreads `...props` over it, so ours would silently clobber the
            error description. The visibility footer points the submit button at it instead.
          */}
          <Select
            label="Status"
            value={values.status}
            error={errors.status}
            fullWidth
            options={EVENT_STATUS_OPTIONS}
            onChange={e => setField("status", e.target.value as EventStatus)}
          />
        </div>

        <div className={styles.formRow}>
          <Input
            label="Start date"
            type="date"
            value={values.startDate}
            error={errors.startDate}
            fullWidth
            onChange={e => setField("startDate", e.target.value)}
          />
          <Input
            label="Start time"
            type="time"
            value={values.startTime}
            error={errors.startTime}
            fullWidth
            onChange={e => setField("startTime", e.target.value)}
          />
        </div>

        {startDateIsPast && (
          <p className={styles.warning} role="alert">
            {PAST_DATE_WARNING}
          </p>
        )}

        <Select
          label="Time zone"
          value={values.timezone}
          error={errors.timezone}
          fullWidth
          options={zones}
          onChange={e => setField("timezone", e.target.value)}
        />

        <Input
          label="Short location"
          value={values.locationShort}
          error={errors.locationShort}
          fullWidth
          helperText={
            locationRequired
              ? "The city-and-country line shown beside the date, e.g. Kuala Lumpur, Malaysia"
              : "Optional for a Webinar, which has no city"
          }
          onChange={e => setField("locationShort", e.target.value)}
        />

        <Textarea
          label="Venue address"
          value={values.venueAddress}
          error={errors.venueAddress}
          rows={2}
          fullWidth
          helperText="Optional. The fuller address shown in the body of the card."
          onChange={e => setField("venueAddress", e.target.value)}
        />

        <EventImageUploadField
          label="Featured image"
          value={values.featuredImage}
          slug={values.title}
          onChange={(ref: ImageRef | null) => setField("featuredImage", ref)}
          onUploadingChange={setImageUploading}
        />

        <Input
          label="Link URL"
          type="url"
          value={values.linkUrl}
          error={errors.linkUrl}
          fullWidth
          helperText="Any absolute http(s) destination — a WordPress page or an EventsAir microsite."
          onChange={e => setField("linkUrl", e.target.value)}
        />

        {saveError && (
          <p className={styles.error} role="alert">
            {saveError}
          </p>
        )}

        {awaitingPastDateConfirm && (
          <p className={styles.warning} role="alert">
            This event starts in the past. Save it anyway?
          </p>
        )}

        {/*
          Last thing before the buttons, so the hairline rule on `.visibility` brackets the state
          and the button that applies it as one form footer — the placement the Hero Banner screen
          uses. Publishing and hiding only ever happen on save, so the sentence names what the
          click will do. The meaning is carried by the badge's own words; the tone class only
          reinforces them, and a reader who cannot see colour loses nothing.
        */}
        <section className={styles.visibility} aria-labelledby="event-visibility-heading">
          <div className={styles.visibilityHeader}>
            <h2 className={styles.visibilityHeading} id="event-visibility-heading">
              Visibility
            </h2>
            <span
              id={VISIBILITY_BADGE_ID}
              className={`${styles.visibilityBadge} ${styles[`visibility_${visibility.tone}`]}`}
              role="status"
            >
              {visibility.badge}
            </span>
          </div>
          <p id={VISIBILITY_CONSEQUENCE_ID} className={styles.visibilityHint}>
            {visibility.consequence}
          </p>
        </section>

        {imageUploading && (
          <p id={UPLOAD_PENDING_ID} className={styles.pendingUpload} role="status">
            The featured image is still uploading. Save is off until it finishes, so the event is
            never saved with the old image.
          </p>
        )}

        <div className={styles.formActions}>
          <Button
            type="button"
            variant="outlined"
            color="default"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          {awaitingPastDateConfirm ? (
            <Button
              type="button"
              variant="contained"
              color="primary"
              onClick={confirmPastDateAndSave}
              disabled={saving || imageUploading}
              aria-describedby={submitDescribedBy}
            >
              {saving ? "Saving…" : "Save anyway"}
            </Button>
          ) : (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving || imageUploading}
              aria-describedby={submitDescribedBy}
            >
              {saving ? "Saving…" : "Save event"}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default EventFormModal;
